import {
  Button,
  EmptyState,
  FlexBox,
  IconButton,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';

import { RenderTemplateContext, templatesApi } from 'apis/templates';
import { ShadowDom } from 'components/shadowDom';
// eslint-disable-next-line no-restricted-imports
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';

import { getLanguageSpecificContent } from '../../util/getLanguageSpecificContent';
import { EnhancedTemplate } from '../../util/useQuickReplyData';

import styles from './quickRepliesPreview.module.scss';
import { templateDiff } from './utils/templateDiff';

type Props = {
  template: EnhancedTemplate | undefined;
  language: string;
  isLoading: boolean;
  /**
   * Callback to insert the contents of the selected template into the
   * editor/form.
   */
  onApplyTemplate: () => void;
  getRenderContext: () =>
    | { context: Partial<RenderTemplateContext> }
    | { csid: string | number };
};

export const QuickRepliesPreview = ({
  template,
  language,
  isLoading,
  onApplyTemplate,
  getRenderContext,
}: Props): JSX.Element | null => {
  const name = template?.name;
  const isRecommended = template?.sourceList === 'recommendations';
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const preRenderContent = getLanguageSpecificContent(language, template) || '';
  const renderResult = templatesApi.useRenderTemplateQuery(
    {
      ...getRenderContext(),
      content: preRenderContent,
    },
    {
      skip: !template || isLoading,
    },
  );

  const showLoading = renderResult.isFetching || isLoading;
  const showNoContentAvailable = !showLoading && !template;
  const showTemplate = !showLoading && template;

  const renderedContent = renderResult.data?.data.rendered_content
    ? templateDiff(
        preRenderContent,
        renderResult.data?.data.rendered_content,
        styles.highlight,
      )
    : preRenderContent;

  return (
    <FlexBox
      borderWidth="0 0 0 1px"
      borderColor="neutral.200"
      padding="300"
      height="320px"
      width="360px"
      flexDirection="column"
      justifyContent="center"
    >
      {showLoading && (
        <FlexBox flexDirection="column" gap="8px" alignItems="center">
          <Spinner size={32} />
          <Text.Paragraph variant="2">Loading template...</Text.Paragraph>
        </FlexBox>
      )}
      {showNoContentAvailable && (
        <EmptyState
          title="No content available"
          description={
            <FlexBox flexDirection="column" gap="300">
              <Text.Paragraph>No template is available</Text.Paragraph>
            </FlexBox>
          }
          icon="ban"
        />
      )}
      {showTemplate && (
        <>
          <FlexBox flexDirection="column">
            <Text.Heading variant="heading-2" className={styles.title}>
              {name}
            </Text.Heading>
            <Text.Paragraph variant="4" className={styles.explainer}>
              The following text will be added:
            </Text.Paragraph>
          </FlexBox>
          <FlexBox flex="1 1 auto" className={styles.content}>
            <ShadowDom mode="closed" tabIndex="-1">
              <SafeInnerHtmlSetter
                element="div"
                profile="email"
                content={renderedContent}
              />
            </ShadowDom>
          </FlexBox>
          <FlexBox
            flex="0 0 auto"
            justifyContent="space-between"
            className={styles.actions}
          >
            <FlexBox alignContent="center">
              {isRecommended &&
                (feedbackSent ? (
                  <Text.Paragraph variant="4" className={styles.feedbackSent}>
                    Thank you for your feedback!
                  </Text.Paragraph>
                ) : (
                  <>
                    <IconButton
                      icon="thumbs-up"
                      variant="tertiary"
                      onClick={() => {
                        ampli.quickReplyFeedbackForRecommendedRepliesClicked({
                          feedback: 'good',
                          score: template?.score,
                        });
                        setFeedbackSent(true);
                      }}
                      tabIndex={-1}
                    />
                    <IconButton
                      icon="thumbs-down"
                      variant="tertiary"
                      onClick={() => {
                        ampli.quickReplyFeedbackForRecommendedRepliesClicked({
                          feedback: 'bad',
                          score: template?.score,
                        });
                        setFeedbackSent(true);
                      }}
                      tabIndex={-1}
                    />
                  </>
                ))}
            </FlexBox>
            <Button variant="primary" onClick={onApplyTemplate}>
              Use this reply
            </Button>
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};
